import * as i1$3 from '@angular/common';
import { formatDate, CommonModule } from '@angular/common';
import * as i2$1 from '@angular/common/http';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Inject, Injectable, Input, Component, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as i2 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import * as i1 from '@auth0/angular-jwt';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as i3 from '@docpath/webcomponents';
import { DpFormModule, DpTabModule, DpFormEncryptionService, SearchOptionType, SearchMode } from '@docpath/webcomponents';
import { DpListTreeHeaderModule } from '@docpath/webcomponents/list-tree-header';
import * as i4 from '@docpath/webcomponents/loading';
import { DpLoadingService } from '@docpath/webcomponents/loading';
import * as i6 from '@docpath/webcomponents/modal';
import { DpModalService } from '@docpath/webcomponents/modal';
import * as i5$1 from '@docpath/webcomponents/toast';
import { DpToast, DpToastService } from '@docpath/webcomponents/toast';
import { DpTreeModule } from '@docpath/webcomponents/tree';
import * as i5 from '@fortawesome/angular-fontawesome';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import * as i1$1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i1$2 from 'ngx-cookie-service';
import { CookieService } from 'ngx-cookie-service';
import { RSAKey } from 'jsencrypt/lib/lib/jsbn/rsa';
import { v4 } from 'uuid';
import { tap, map, catchError, of, iif, concatMap, EMPTY, Observable } from 'rxjs';
import { OktaAuth } from '@okta/okta-auth-js';

class HttpEnvironment {
    accessControlApiPath;
    appName;
    baseUrl;
    cookieName;
    webApiPath;
    constructor(accessControlApiPath, appName, baseUrl, cookieName, webApiPath) {
        this.accessControlApiPath = accessControlApiPath;
        this.appName = appName;
        this.baseUrl = baseUrl;
        this.cookieName = cookieName;
        this.webApiPath = webApiPath;
    }
}

class SessionCookieService {
    jwtService;
    environment;
    cookieName;
    constructor(jwtService, environment) {
        this.jwtService = jwtService;
        this.environment = environment;
        this.cookieName = `${this.environment.cookieName}-session`;
    }
    decodeSessionToken(token) {
        const tokenWithoutBearer = token.slice(7);
        return this.jwtService.decodeToken(tokenWithoutBearer);
    }
    getAuthorization() {
        const session = this.getSessionCookie();
        return session?.authorization;
    }
    getCookie() {
        return window.localStorage.getItem(this.cookieName);
    }
    getSessionCookie() {
        const token = this.getCookie();
        if (token?.startsWith('Bearer ')) {
            return this.decodeSessionToken(token);
        }
        return null;
    }
    getSessionToken(response) {
        return response.headers.get('Authorization');
    }
    getUserLogged() {
        const session = this.getSessionCookie();
        return session?.user;
    }
    refreshSessionCookie(response) {
        const token = this.getSessionToken(response);
        if (token) {
            this.setSessionCookie(token);
        }
    }
    removeSessionCookie() {
        window.localStorage.removeItem(this.cookieName);
    }
    setSessionCookie(userSession) {
        window.localStorage.setItem(this.cookieName, userSession);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SessionCookieService, deps: [{ token: i1.JwtHelperService }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SessionCookieService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SessionCookieService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.JwtHelperService }, { type: HttpEnvironment, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class Constants {
    static DateFormat = 'yyyy-MM-dd HH:mm:ss';
    static Timezone = 'UTC';
}

class UtilService {
    translateService;
    constructor(translateService) {
        this.translateService = translateService;
    }
    camelCaseToHyphen(text) {
        let parsedText = String(text), charIndex = 0;
        parsedText = parsedText.charAt(0).toLowerCase() + parsedText.slice(1);
        [...parsedText].forEach(char => {
            if ((/^[A-Za-z]$/u).test(char) && char === char.toUpperCase()) {
                parsedText = this.replaceAt(parsedText, charIndex, `-${char.toLowerCase()}`);
                charIndex += 2;
            }
            else {
                charIndex += 1;
            }
        });
        return parsedText;
    }
    encryptPass(publicKey, value) {
        const rsa = new RSAKey();
        rsa.setPublic(publicKey, '10001');
        return rsa.encrypt(value);
    }
    decodeBase64(base64String) {
        const binaryString = window.atob(base64String);
        const { length } = binaryString;
        const bytes = new Uint8Array(length);
        // eslint-disable-next-line no-loops/no-loops
        for (let index = 0; index < length; index++) {
            bytes[index] = binaryString.charCodeAt(index);
        }
        return bytes;
    }
    decodeBase64ToString(encoded) {
        const decoded = window.atob(encoded), decodedURIComponent = [...decoded]
            .map(character => this.normalizeCharacter(character))
            .join('');
        return decodeURIComponent(decodedURIComponent);
    }
    formatPropertiesDate(date) {
        if (date) {
            const { currentLang } = this.translateService;
            if (currentLang === 'es') {
                return formatDate(date, Constants.DateFormat, 'es');
            }
            return formatDate(date, Constants.DateFormat, 'en');
        }
        return '';
    }
    formatDateToYYYYMMDD(date) {
        const year = date.getFullYear();
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const day = (`0${date.getDate()}`).slice(-2);
        return `${year}-${month}-${day}`;
    }
    getDateFormats() {
        return {
            en: Constants.DateFormat,
            es: Constants.DateFormat,
            pt: Constants.DateFormat
        };
    }
    generateUuid() {
        return v4();
    }
    matchRoutesWithWildcards(currentPath, paths) {
        const pathRegexes = paths.map(path => new RegExp(`^${path.replace(/\*\*/gu, '[a-zA-Z0-9]+')}$`, 'u'));
        return pathRegexes.some(regex => regex.test(currentPath));
    }
    replaceAt(text, index, replacement) {
        return index + 1 > text.length ? text : text.slice(0, Math.max(0, index)) + replacement + text.slice(Math.max(0, index + 1));
    }
    sort(array, property, itemType, orderType = 'asc') {
        const sortByNumberProperty = (a, b) => {
            const objectA = a, objectB = b;
            return (objectA[property] - objectB[property]) * order;
        }, sortByStringProperty = (a, b) => {
            const objectA = a, objectB = b;
            const valueA = objectA[property].toLowerCase(), valueB = objectB[property].toLowerCase();
            if (valueA > valueB) {
                return 1 * Number(order);
            }
            if (valueA < valueB) {
                return -1 * order;
            }
            return 0;
        }, compare = itemType === 'number' ? sortByNumberProperty : sortByStringProperty, order = orderType.toLowerCase() === 'asc' ? 1 : -1;
        return [...array].sort(compare);
    }
    stringArrayToRecord(array = []) {
        return array.reduce((result, value, index) => {
            result[`param${index}`] = value;
            return result;
        }, {});
    }
    existsTranslation(key) {
        return this.translateService.instant(key) !== key;
    }
    initBrowserDownload(file) {
        const fileContent = this.decodeBase64(file.content);
        const blob = new Blob([fileContent]);
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = file.name;
        link.dispatchEvent(new MouseEvent('click'));
    }
    normalizeCharacter(character) {
        let charCodeHex = character.charCodeAt(0).toString(16);
        if (charCodeHex.length === 1) {
            charCodeHex = `0${charCodeHex}`;
        }
        return `%${charCodeHex}`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: UtilService, deps: [{ token: i1$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: UtilService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: UtilService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.TranslateService }] });

class PageWithTabsComponent {
    sessionCookieService;
    router;
    formNavigationService;
    tabService;
    utilService;
    icon;
    tabs = [];
    title = '';
    navigationSubscription;
    navigationEndSubscription;
    navigationBlocked = false;
    constructor(sessionCookieService, router, formNavigationService, tabService, utilService) {
        this.sessionCookieService = sessionCookieService;
        this.router = router;
        this.formNavigationService = formNavigationService;
        this.tabService = tabService;
        this.utilService = utilService;
        this.navigationSubscription = this.formNavigationService.getNavigationBlockedObservable().subscribe(blocked => {
            this.navigationBlocked = blocked;
        });
        this.navigationEndSubscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                const currentUrl = event.urlAfterRedirects;
                this.activeTab(this.tabs, currentUrl);
            }
        });
    }
    ngOnInit() {
        this.loadTabs(this.tabs);
    }
    ngOnDestroy() {
        this.navigationSubscription.unsubscribe();
        this.navigationEndSubscription.unsubscribe();
    }
    onClickTab(tab) {
        if (this.navigationBlocked) {
            this.router.navigate([tab.link]);
        }
        else {
            this.tabService.clickTab(tab, this.tabs);
        }
    }
    loadTabs(tabs) {
        const paths = this.sessionCookieService.getSessionCookie()?.authorization.paths ?? [], currentUrl = this.router.url;
        this.tabs = tabs
            .filter(tab => tab.link && this.utilService.matchRoutesWithWildcards(tab.link, paths))
            .map(tab => {
            tab.active = this.areRoutesEqual(tab.link ?? '', currentUrl);
            return tab;
        });
    }
    activeTab(tabs, currentUrl) {
        tabs.forEach(tab => {
            tab.active = (tab.link === currentUrl);
        });
    }
    areRoutesEqual(route1, route2) {
        const segments1 = route1.split('/');
        const segments2 = route2.split('/');
        if (segments1.length !== segments2.length) {
            return false;
        }
        return segments1.every((segment, index) => segment === '**' || segments2[index] === '**' || segment === segments2[index]);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: PageWithTabsComponent, deps: [{ token: SessionCookieService }, { token: i2.Router }, { token: i3.DpFormNavigationService }, { token: i3.DpTabService }, { token: UtilService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.3", type: PageWithTabsComponent, selector: "linces-page-with-tabs", inputs: { icon: "icon", tabs: "tabs", title: "title" }, ngImport: i0, template: "@if (title) {\n    <span class=\"seccion\">\n        <fa-icon [icon]=\"icon\"\n                class=\"fa-1x me-4 text-dpmedium\"\n                size=\"1x\"></fa-icon>\n        {{ title | translate }}\n        <ng-content select=\"custom-header\"></ng-content>\n    </span>\n\n    <hr class=\"mb-3\">\n}\n\n<div class=\"row m-0\">\n    <div class=\"col-12 p-0\">\n        <dp-tab [tabs]=\"tabs\"\n                [customClick]=\"true\"\n                (clickedTab)=\"onClickTab($event)\"></dp-tab>\n        <div class=\"tab-content\">\n            <div class=\"tab-pane fade show active\"\n                role=\"tabpanel\"\n                aria-labelledby=\"nav-general-tab\">\n                <router-outlet></router-outlet>\n            </div>\n        </div>\n    </div>\n</div>", dependencies: [{ kind: "component", type: i3.DpTabComponent, selector: "dp-tab", inputs: ["tabs", "customClick"], outputs: ["clickedTab"] }, { kind: "component", type: i5.FaIconComponent, selector: "fa-icon", inputs: ["icon", "title", "animation", "mask", "flip", "size", "pull", "border", "inverse", "symbol", "rotate", "fixedWidth", "transform", "a11yRole"] }, { kind: "directive", type: i2.RouterOutlet, selector: "router-outlet", inputs: ["name"], outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }, { kind: "pipe", type: i1$1.TranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: PageWithTabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'linces-page-with-tabs', template: "@if (title) {\n    <span class=\"seccion\">\n        <fa-icon [icon]=\"icon\"\n                class=\"fa-1x me-4 text-dpmedium\"\n                size=\"1x\"></fa-icon>\n        {{ title | translate }}\n        <ng-content select=\"custom-header\"></ng-content>\n    </span>\n\n    <hr class=\"mb-3\">\n}\n\n<div class=\"row m-0\">\n    <div class=\"col-12 p-0\">\n        <dp-tab [tabs]=\"tabs\"\n                [customClick]=\"true\"\n                (clickedTab)=\"onClickTab($event)\"></dp-tab>\n        <div class=\"tab-content\">\n            <div class=\"tab-pane fade show active\"\n                role=\"tabpanel\"\n                aria-labelledby=\"nav-general-tab\">\n                <router-outlet></router-outlet>\n            </div>\n        </div>\n    </div>\n</div>" }]
        }], ctorParameters: () => [{ type: SessionCookieService }, { type: i2.Router }, { type: i3.DpFormNavigationService }, { type: i3.DpTabService }, { type: UtilService }], propDecorators: { icon: [{
                type: Input
            }], tabs: [{
                type: Input
            }], title: [{
                type: Input
            }] } });

class CustomHttpResponse {
    headers;
    result;
    error;
    constructor(headers, result, error) {
        this.headers = headers;
        this.result = result;
        this.error = error;
    }
}

class ResponseError {
    message;
    statusCode;
    status;
    logErrorCode;
    messageParams;
    constructor(message, statusCode, status, logErrorCode, messageParameters) {
        this.message = message;
        this.statusCode = statusCode;
        this.status = status;
        this.logErrorCode = logErrorCode;
        this.messageParams = messageParameters;
    }
}

class HttpService {
    sessionCookieService;
    http;
    router;
    loadingService;
    toastService;
    modalService;
    translateService;
    environment;
    baseUrl;
    appName;
    // eslint-disable-next-line max-params
    constructor(sessionCookieService, http, router, loadingService, toastService, modalService, translateService, environment) {
        this.sessionCookieService = sessionCookieService;
        this.http = http;
        this.router = router;
        this.loadingService = loadingService;
        this.toastService = toastService;
        this.modalService = modalService;
        this.translateService = translateService;
        this.environment = environment;
        this.baseUrl = this.environment.baseUrl || document.querySelectorAll('base')[0].href;
        this.appName = this.environment.appName;
    }
    delete(options) {
        const url = this.createUrl(options), request = this.http.delete(url, this.getOptions());
        return this.createRequest(request, options);
    }
    get(options) {
        const url = this.createUrl(options), request = this.http.get(url, this.getOptions());
        return this.createRequest(request, options);
    }
    post(body, options) {
        const url = this.createUrl(options), request = this.http.post(url, body, this.getOptions());
        return this.createRequest(request, options);
    }
    put(body, options) {
        const url = this.createUrl(options), request = this.http.put(url, body, this.getOptions());
        return this.createRequest(request, options);
    }
    patch(body, options) {
        const url = this.createUrl(options), request = this.http.patch(url, body, this.getOptions());
        return this.createRequest(request, options);
    }
    createRequest(request, options) {
        const customRequest = this.addPipe(request, options);
        this.addSuscribe(customRequest, options);
        return customRequest;
    }
    createUrl(options) {
        const { relativeUrl } = options;
        return `${this.baseUrl}${relativeUrl}${this.getQueryParams(options)}`;
    }
    getQueryParams(options) {
        const { queryParameters } = options;
        if (queryParameters) {
            const parameters = new URLSearchParams();
            Object.keys(queryParameters).forEach(key => {
                if (Array.isArray(queryParameters[key])) {
                    queryParameters[key].forEach(value => {
                        parameters.append(key, value);
                    });
                }
                else {
                    parameters.append(key, queryParameters[key]);
                }
            });
            return `?${parameters.toString()}`;
        }
        return '';
    }
    addPipe(request, options) {
        let customResponse;
        const requestPipe = request.pipe(tap(response => {
            let result;
            switch (response.status) {
                case 200:
                case 201:
                case 204:
                case 205:
                    result = response.body?.result;
                    break;
                default:
                    break;
            }
            customResponse = new CustomHttpResponse(response.headers, result);
        }), map(() => (options.map ? options.map(customResponse) : customResponse)), catchError(response => {
            const error = this.processResponseError(response);
            customResponse = new CustomHttpResponse(response.headers, undefined, error);
            return of(customResponse);
        }));
        return iif(() => typeof options.concatMap === 'function', requestPipe.pipe(concatMap(() => (options.concatMap ? options.concatMap(customResponse) : EMPTY))), requestPipe);
    }
    processResponseError(response) {
        if (typeof response.error?.error === 'object') {
            const error = response.error?.error;
            if (error.message === 'conflict' && error.messageParams) {
                error.messageParams = [error.messageParams.reduce((result, parameter, index) => {
                        if (index === 0) {
                            return parameter;
                        }
                        return `${result}, ${parameter}`;
                    }, '')];
            }
            error.message = `linces.http.error.${error.message}`;
            if (this.translateService.instant(error.message) === error.message) {
                error.message = 'linces.http.error.unknown';
            }
            return error;
        }
        switch (response.status) {
            case 0:
                return new ResponseError('linces.http.error.connection-refused', 0, '');
            case 400:
                return new ResponseError('linces.http.error.bad-request', 400, '');
            case 404:
                return new ResponseError('linces.http.error.not-found', 400, '', undefined, [response.error?.path]);
            default:
                return new ResponseError('linces.http.error.unknown', response.status, '');
        }
    }
    addSuscribe(request, options) {
        if (options.suscribe) {
            const { loading } = options;
            if (loading) {
                this.loadingService.open();
            }
            request.subscribe(response => {
                if (loading) {
                    this.loadingService.close();
                }
                this.processResponse(response, options);
            });
        }
    }
    processResponse(response, options) {
        if (response.error) {
            this.onResponseError(response.error, options, response);
        }
        else {
            this.onResponseSuccess(response, options);
        }
    }
    onResponseError(error, options, response) {
        const { suscribeOnError } = options;
        const messageParameters = error.messageParams?.reduce((result, parameter, index) => {
            result[`param${index}`] = parameter;
            return result;
        }, {});
        if (options.catchError) {
            const { message, statusCode } = error;
            options.catchError(statusCode, message, messageParameters);
        }
        else {
            if (error.statusCode === 401) {
                this.router.navigate(['/login']);
            }
            else {
                this.modalService.openError(error.message, messageParameters);
            }
            if (suscribeOnError) {
                suscribeOnError(response);
            }
        }
    }
    onResponseSuccess(response, options) {
        const { suscribe, toast, modalHead, modalMessage } = options;
        this.sessionCookieService.refreshSessionCookie(response);
        if (toast) {
            this.toastService.showSuccess(new DpToast(this.appName, toast));
        }
        if (modalMessage && modalHead && response.result?.importResume) {
            const messageParameters = {
                success: response.result.importResume.success,
                error: response.result.importResume.error
            };
            this.modalService.openInfo(modalMessage, messageParameters, modalHead);
        }
        if (suscribe) {
            suscribe(response);
        }
    }
    getHeaders() {
        const session = this.sessionCookieService.getCookie();
        return session ? new HttpHeaders({ 'Authorization': session }) : new HttpHeaders();
    }
    getOptions() {
        return { headers: this.getHeaders(), observe: 'response' };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: HttpService, deps: [{ token: SessionCookieService }, { token: i2$1.HttpClient }, { token: i2.Router }, { token: i4.DpLoadingService }, { token: i5$1.DpToastService }, { token: i6.DpModalService }, { token: i1$1.TranslateService }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: HttpService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: HttpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: SessionCookieService }, { type: i2$1.HttpClient }, { type: i2.Router }, { type: i4.DpLoadingService }, { type: i5$1.DpToastService }, { type: i6.DpModalService }, { type: i1$1.TranslateService }, { type: HttpEnvironment, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class User {
    id;
    name;
    role;
    active;
    sessionId;
    email;
    fullName;
    credentials;
    // eslint-disable-next-line max-params
    constructor(id, name, role, active, sessionId, email, fullName, credentials) {
        this.id = id;
        this.name = name;
        this.role = role;
        this.active = active;
        this.sessionId = sessionId;
        this.email = email;
        this.fullName = fullName;
        this.credentials = credentials;
    }
}
var UserRole;
(function (UserRole) {
    UserRole["Admin"] = "admin";
    UserRole["Normal"] = "normal";
    UserRole["Local"] = "local";
})(UserRole || (UserRole = {}));
class Credentials {
    access_token;
    refresh_token;
    expires_in;
    constructor(access_token, refresh_token, expires_in) {
        this.access_token = access_token;
        this.refresh_token = refresh_token;
        this.expires_in = expires_in;
    }
}
var Cookie;
(function (Cookie) {
    Cookie["AccessToken"] = "docpath_accesstoken";
    Cookie["RefreshToken"] = "docpath_refreshtoken";
})(Cookie || (Cookie = {}));

class CredentialsCookiesService {
    cookieService;
    constructor(cookieService) {
        this.cookieService = cookieService;
    }
    getAccessCookie() {
        return this.cookieService.get(Cookie.AccessToken);
    }
    checkAccessCookie() {
        return this.cookieService.check(Cookie.AccessToken);
    }
    getRefreshCookie() {
        return this.cookieService.get(Cookie.RefreshToken);
    }
    checkRefreshCookie() {
        return this.cookieService.check(Cookie.RefreshToken);
    }
    store(credentials) {
        this.cookieService.set(Cookie.AccessToken, credentials.access_token, {
            expires: new Date(new Date().getTime() + credentials.expires_in * 1000),
            path: '/'
        });
        this.cookieService.set(Cookie.RefreshToken, credentials.refresh_token, {
            expires: new Date(new Date().getTime() + 24 * 3600 * 1000), //24 hours
            path: '/'
        });
    }
    delete() {
        this.cookieService.delete(Cookie.AccessToken, '/');
        this.cookieService.delete(Cookie.RefreshToken, '/');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CredentialsCookiesService, deps: [{ token: i1$2.CookieService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CredentialsCookiesService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CredentialsCookiesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.CookieService }] });

class LoginHttpService {
    httpService;
    utilService;
    environment;
    relativeUrl;
    constructor(httpService, utilService, environment) {
        this.httpService = httpService;
        this.utilService = utilService;
        this.environment = environment;
        this.relativeUrl = this.environment.accessControlApiPath;
    }
    getProviders(options) {
        this.httpService.get({
            relativeUrl: `${this.relativeUrl}/providers`,
            loading: true,
            catchError: options.catchError,
            suscribe: options.suscribe
        });
    }
    getSession() {
        const searchParameters = new URLSearchParams(location.search), token = searchParameters.get('token'), queryParameters = token ? { token } : {};
        return this.httpService
            .get({
            relativeUrl: `${this.relativeUrl}/session`,
            queryParameters,
            loading: true
        })
            .pipe(map((response) => response));
    }
    getSessionByToken(accessToken, refreshToken) {
        const queryParameters = { accessToken, refreshToken };
        return this.httpService
            .get({
            relativeUrl: `${this.relativeUrl}/session-cloud`,
            queryParameters,
            loading: true
        })
            .pipe(map((response) => response));
    }
    getUserApplications() {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/aim/user-applications`,
                loading: false,
                suscribe: response => {
                    const apps = response.result;
                    observer.next(apps);
                    observer.complete();
                }
            });
        });
    }
    login(options) {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/public-key`,
                loading: true,
                catchError: (statusCode, message) => {
                    const parsedMessage = this.processErrorMessage(statusCode, message);
                    if (options.catchError) {
                        options.catchError(statusCode, parsedMessage);
                    }
                },
                concatMap: response => {
                    if (!response.error && options.queryParameters) {
                        const password = options.queryParameters['pass'], publicKey = response.result, encryptedPass = this.utilService.encryptPass(publicKey, password);
                        options.queryParameters['pass'] = encryptedPass;
                        return this.createLoginRequest(options);
                    }
                    return of(response);
                },
                suscribe: response => {
                    observer.next(response);
                    observer.complete();
                }
            });
        });
    }
    loginByToken(options) {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/login-cloud`,
                queryParameters: options.queryParameters,
                catchError: (statusCode, message) => {
                    const parsedMessage = this.processErrorMessage(statusCode, message);
                    if (options.catchError) {
                        options.catchError(statusCode, parsedMessage);
                    }
                },
                suscribe: response => {
                    observer.next(response);
                    observer.complete();
                }
            });
        });
    }
    loginWithOkta(options) {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/login-okta`,
                loading: true,
                queryParameters: options.queryParameters,
                catchError: (statusCode, message) => {
                    const parsedMessage = this.processErrorMessage(statusCode, message);
                    if (options.catchError) {
                        options.catchError(statusCode, parsedMessage);
                    }
                },
                suscribe: response => {
                    observer.next(response);
                    observer.complete();
                }
            });
        });
    }
    logout() {
        return new Observable(observer => {
            this.httpService.get({
                relativeUrl: `${this.relativeUrl}/logout`,
                loading: true,
                suscribe: response => {
                    observer.next(response);
                    observer.complete();
                }
            });
        });
    }
    createLoginRequest(options) {
        return this.httpService.get({
            relativeUrl: `${this.relativeUrl}/login`,
            loading: true,
            queryParameters: options.queryParameters,
            catchError: (statusCode, message) => this.processErrorMessage(statusCode, message)
        });
    }
    processErrorMessage(statusCode, message) {
        if (statusCode === 400 && message === 'required-field') {
            return 'linces.login.error.invalid-okta-token';
        }
        if (statusCode === 401) {
            return 'linces.login.error.unauthorized';
        }
        if (statusCode === 500) {
            const lastWord = message.slice(message.lastIndexOf('.') + 1);
            switch (lastWord) {
                case 'aim-connection':
                    return 'linces.login.error.aim-connection';
                case 'invalid-aim-url':
                    return 'linces.login.error.invalid-aim-url';
                case 'invalid-aim-app-token':
                    return 'linces.login.error.invalid-aim-app-token';
                default:
                    return message;
            }
        }
        return message;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginHttpService, deps: [{ token: HttpService }, { token: UtilService }, { token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginHttpService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginHttpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: HttpService }, { type: UtilService }, { type: HttpEnvironment, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class LincesCommonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LincesCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.3", ngImport: i0, type: LincesCommonModule, declarations: [PageWithTabsComponent], imports: [CommonModule,
            DpFormModule,
            DpListTreeHeaderModule,
            DpTabModule,
            DpTreeModule,
            FontAwesomeModule,
            FormsModule,
            NgbModalModule,
            RouterModule,
            TranslateModule], exports: [PageWithTabsComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LincesCommonModule, providers: [
            DpFormEncryptionService,
            DpLoadingService,
            DpModalService,
            DpToastService,
            HttpService,
            HttpClient,
            JwtHelperService,
            LoginHttpService,
            CookieService,
            CredentialsCookiesService,
            SessionCookieService
        ], imports: [CommonModule,
            DpFormModule,
            DpListTreeHeaderModule,
            DpTabModule,
            DpTreeModule,
            FontAwesomeModule,
            FormsModule,
            NgbModalModule,
            RouterModule,
            TranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LincesCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PageWithTabsComponent
                    ],
                    imports: [
                        CommonModule,
                        DpFormModule,
                        DpListTreeHeaderModule,
                        DpTabModule,
                        DpTreeModule,
                        FontAwesomeModule,
                        FormsModule,
                        NgbModalModule,
                        RouterModule,
                        TranslateModule
                    ],
                    providers: [
                        DpFormEncryptionService,
                        DpLoadingService,
                        DpModalService,
                        DpToastService,
                        HttpService,
                        HttpClient,
                        JwtHelperService,
                        LoginHttpService,
                        CookieService,
                        CredentialsCookiesService,
                        SessionCookieService
                    ],
                    exports: [
                        PageWithTabsComponent
                    ]
                }]
        }] });

class CardsInfoFiltersConfig {
    cardsUsed;
    name;
    constructor({ cardsUsed, name }) {
        this.cardsUsed = cardsUsed;
        this.name = name;
    }
}
class CardInfoFiltersConfig {
    mainFilter;
    position;
    secundaryFilter;
    constructor({ mainFilter, position, secundaryFilter }) {
        this.mainFilter = mainFilter;
        this.position = position;
        this.secundaryFilter = secundaryFilter;
    }
}

class LocalConfig {
    cardsInfoFilters;
    tables;
    constructor({ cardsInfoFilters, tables }) {
        this.cardsInfoFilters = cardsInfoFilters;
        this.tables = tables;
    }
}

class CardsInfoFiltersService {
    environment;
    cookieName;
    constructor(environment) {
        this.environment = environment;
        this.cookieName = `${this.environment.cookieName}-local-config`;
    }
    getCardByPosition(cardsName, position) {
        return this.getCards(cardsName).find(card => card.position === position) || new CardInfoFiltersConfig({ mainFilter: '', position: -1, secundaryFilter: -1 });
    }
    getCards(cardsName) {
        const cardsConfig = this.getCardsConfig(cardsName), cards = cardsConfig?.cardsUsed || [];
        return cards;
    }
    getCardsConfig(cardsName) {
        const localConfig = window.localStorage.getItem(this.cookieName);
        let cardsConfig;
        if (localConfig) {
            const decodedConfig = JSON.parse(atob(localConfig));
            cardsConfig = decodedConfig.cardsInfoFilters?.find(cardsInfoFilters => cardsInfoFilters.name === cardsName);
        }
        if (cardsConfig) {
            return cardsConfig;
        }
        return new CardsInfoFiltersConfig({ cardsUsed: [], name: cardsName });
    }
    updateCards(cardsName, cardsUsed) {
        const cardsConfig = this.getCardsConfig(cardsName);
        cardsConfig.cardsUsed = cardsUsed;
        this.updateCardsConfig(cardsConfig);
    }
    updateCard(cardsName, card) {
        const cardsConfig = this.getCardsConfig(cardsName), cardIndex = cardsConfig.cardsUsed.findIndex(c => c.position === card.position);
        if (cardIndex !== -1) {
            cardsConfig.cardsUsed.splice(cardIndex, 1, card);
            this.updateCardsConfig(cardsConfig);
        }
    }
    setConfigCookie(config) {
        window.localStorage.setItem(this.cookieName, btoa(JSON.stringify(config)));
    }
    updateCardsConfig(cardsConfig) {
        const localConfig = window.localStorage.getItem(this.cookieName);
        if (localConfig) {
            const decodedConfig = JSON.parse(atob(localConfig));
            if (decodedConfig.cardsInfoFilters) {
                const cardsIndex = decodedConfig.cardsInfoFilters?.findIndex(cardsInfoFilters => cardsInfoFilters.name === cardsConfig.name);
                if (cardsIndex !== -1) {
                    decodedConfig.cardsInfoFilters?.splice(cardsIndex, 1, cardsConfig);
                }
                else {
                    decodedConfig.cardsInfoFilters?.push(cardsConfig);
                }
            }
            else {
                decodedConfig.cardsInfoFilters = [cardsConfig];
            }
            this.setConfigCookie(decodedConfig);
        }
        else {
            const newConfig = new LocalConfig({ cardsInfoFilters: [cardsConfig] });
            this.setConfigCookie(newConfig);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CardsInfoFiltersService, deps: [{ token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CardsInfoFiltersService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: CardsInfoFiltersService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: HttpEnvironment, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class CustomHttpOptions {
    relativeUrl;
    queryParameters;
    loading;
    suscribe;
    suscribeOnError;
    toast;
    catchError;
    concatMap;
    map;
    tap;
    modalHead;
    modalMessage;
    // eslint-disable-next-line max-params
    constructor(relativeUrl, queryParameters, loading, suscribe, suscribeOnError, toast, catchError, concatMap, map, tap, modalHead, modalMessage) {
        this.relativeUrl = relativeUrl;
        this.queryParameters = queryParameters;
        this.loading = loading;
        this.suscribe = suscribe;
        this.suscribeOnError = suscribeOnError;
        this.toast = toast;
        this.catchError = catchError;
        this.concatMap = concatMap;
        this.map = map;
        this.tap = tap;
        this.modalHead = modalHead;
        this.modalMessage = modalMessage;
    }
}

var OktaErrors;
(function (OktaErrors) {
    OktaErrors["ClosePopup"] = "Unable to parse OAuth flow response";
    OktaErrors["Pkce"] = "PKCE requires a modern browser with encryption support running in a secure context";
    OktaErrors["RedirectUri"] = "The 'redirect_uri' parameter must be a Login redirect URI in the client app settings";
    OktaErrors["UserNoAccess"] = "User is not assigned to the client application";
})(OktaErrors || (OktaErrors = {}));

class LoginService {
    formEncryptionService;
    loginHttpService;
    router;
    sessionCookieService;
    oktaAuth;
    constructor(formEncryptionService, loginHttpService, router, sessionCookieService) {
        this.formEncryptionService = formEncryptionService;
        this.loginHttpService = loginHttpService;
        this.router = router;
        this.sessionCookieService = sessionCookieService;
    }
    checkSession(currentUrl, redirectPath) {
        return this.loginHttpService.getSession().pipe(map(response => {
            const token = this.sessionCookieService.getSessionToken(response);
            if (!token) {
                this.sessionCookieService.removeSessionCookie();
                return currentUrl === redirectPath ? true : this.router.parseUrl(redirectPath);
            }
            const session = this.sessionCookieService.decodeSessionToken(token);
            this.sessionCookieService.setSessionCookie(token);
            if (this.isAuthorizationPath(currentUrl, session.authorization)) {
                this.initFormEncryptionService();
                return true;
            }
            const parentPath = currentUrl.slice(0, currentUrl.lastIndexOf('/'));
            if (parentPath) {
                const allowedChildRoute = session.authorization.paths.find((path) => path.startsWith(`${parentPath}/`));
                if (allowedChildRoute) {
                    return this.router.parseUrl(allowedChildRoute);
                }
            }
            return this.router.parseUrl(session.authorization.redirectPath);
        }), catchError(() => of(this.router.parseUrl(redirectPath))));
    }
    checkSessionByToken(accessToken, refreshToken, currentUrl, redirectPath) {
        return this.loginHttpService.getSessionByToken(accessToken, refreshToken).pipe(map(response => {
            const token = this.sessionCookieService.getSessionToken(response);
            if (!token) {
                this.sessionCookieService.removeSessionCookie();
                return currentUrl === redirectPath ? true : this.router.parseUrl(redirectPath);
            }
            const session = this.sessionCookieService.decodeSessionToken(token);
            this.sessionCookieService.setSessionCookie(token);
            if (this.isAuthorizationPath(currentUrl, session.authorization)) {
                this.initFormEncryptionService();
                return true;
            }
            const parentPath = currentUrl.slice(0, currentUrl.lastIndexOf('/'));
            if (parentPath) {
                const allowedChildRoute = session.authorization.paths.find((path) => path.startsWith(`${parentPath}/`));
                if (allowedChildRoute) {
                    return this.router.parseUrl(allowedChildRoute);
                }
            }
            return this.router.parseUrl(session.authorization.redirectPath);
        }), catchError(() => of(this.router.parseUrl(redirectPath))));
    }
    closeOktaSesion() {
        if (this.oktaAuth) {
            this.oktaAuth.revokeAccessToken();
            this.oktaAuth.closeSession();
        }
    }
    getOktaToken(options, oktaProvider, callbackUrl, postLogout) {
        const oktaAuth = this.initOktaAuth(oktaProvider, callbackUrl, postLogout);
        oktaAuth.token.getWithPopup({ scopes: oktaProvider.scopes.split(',') }).then(tokenContainer => {
            const accessToken = tokenContainer.tokens.accessToken?.accessToken;
            this.oktaAuth = oktaAuth;
            this.loginWithOkta({
                queryParameters: { accessToken },
                catchError: (statusCode, message) => {
                    if (options.catchError) {
                        options.catchError(statusCode, message);
                    }
                    this.closeOktaSesion();
                },
                suscribe: options.suscribe
            });
        }).catch(error => {
            const message = error.message.split(/[.:]/u)[0];
            if (message) {
                const parsedMessage = this.processOktaError(message);
                if (options.catchError) {
                    options.catchError(error.statusCode, parsedMessage);
                }
            }
        });
    }
    getProviders(options) {
        this.loginHttpService.getProviders(options);
    }
    getUserApplications() {
        return this.loginHttpService.getUserApplications();
    }
    login(options) {
        this.loginHttpService.login(options).subscribe(response => {
            this.loginSuscribe(response, options);
        });
    }
    loginByToken(options) {
        this.loginHttpService.loginByToken(options).subscribe(response => {
            this.loginSuscribe(response, options);
        });
    }
    loginWithOkta(options) {
        this.loginHttpService.loginWithOkta(options).subscribe(response => {
            this.loginSuscribe(response, options);
        });
    }
    logout() {
        this.loginHttpService.logout().subscribe(() => {
            this.sessionCookieService.removeSessionCookie();
            this.oktaLogout();
            this.router.navigate(['/login']);
        });
    }
    oktaLogout() {
        if (this.oktaAuth) {
            this.oktaAuth.signOut();
            this.oktaAuth.tokenManager.clear();
            this.oktaAuth = undefined;
        }
    }
    initFormEncryptionService() {
        const session = this.sessionCookieService.getSessionCookie();
        if (session?.authorization.publicKey) {
            this.formEncryptionService.setRsaPublicKey(session.authorization.publicKey);
        }
    }
    initOktaAuth(provider, callbackUrl, postLogout) {
        const defaultOriginalUri = window.location.origin;
        return new OktaAuth({
            clientId: provider.providerClientId,
            issuer: provider.authorizationServerUrl,
            redirectUri: `${defaultOriginalUri}${callbackUrl}`,
            postLogoutRedirectUri: `${defaultOriginalUri}${postLogout}`,
            pkce: provider.pkce
        });
    }
    isAuthorizationPath(currentPath, authorization) {
        const { paths } = authorization;
        if (!paths) {
            return false;
        }
        const pathRegexes = paths.map(path => new RegExp(`^${path.replace(/\*\*/gu, '[a-zA-Z0-9]+')}$`, 'u'));
        return pathRegexes.some(regex => regex.test(currentPath));
    }
    loginSuscribe(response, options) {
        const token = this.sessionCookieService.getSessionToken(response);
        if (token) {
            this.sessionCookieService.setSessionCookie(token);
        }
        if (options.suscribe) {
            options.suscribe(response);
        }
    }
    processOktaError(message) {
        let errorMessage = '';
        switch (message) {
            case OktaErrors.Pkce:
                errorMessage = 'linces.login.okta-errors.pkce';
                break;
            case OktaErrors.RedirectUri:
                errorMessage = 'linces.login.okta-errors.invalid-redirect-uri';
                break;
            case OktaErrors.UserNoAccess:
                errorMessage = 'linces.login.okta-errors.user-no-access';
                break;
            case OktaErrors.ClosePopup:
                break;
            default:
                errorMessage = 'linces.login.okta-errors.unknown';
                break;
        }
        return errorMessage;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginService, deps: [{ token: i3.DpFormEncryptionService }, { token: LoginHttpService }, { token: i2.Router }, { token: SessionCookieService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: LoginService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i3.DpFormEncryptionService }, { type: LoginHttpService }, { type: i2.Router }, { type: SessionCookieService }] });

class Provider {
    type;
    providerClientId;
    domain;
    scopes;
    authorizationServerUrl;
    activeSSO;
    pkce;
    // eslint-disable-next-line max-params
    constructor(type, providerClientId, domain, scopes, authorizationServerUrl, activeSSO, pkce) {
        this.type = type;
        this.providerClientId = providerClientId;
        this.domain = domain;
        this.scopes = scopes;
        this.authorizationServerUrl = authorizationServerUrl;
        this.activeSSO = activeSSO;
        this.pkce = pkce;
    }
}
var ProviderTitle;
(function (ProviderTitle) {
    ProviderTitle["Okta"] = "OKTA";
})(ProviderTitle || (ProviderTitle = {}));

class SessionCookie {
    user;
    authorization;
    services;
    app;
    constructor(user, authorization, services, app) {
        this.user = user;
        this.authorization = authorization;
        this.services = services;
        this.app = app;
    }
}
var AppService;
(function (AppService) {
    AppService["Aim"] = "aim";
    AppService["Integra"] = "integra";
})(AppService || (AppService = {}));

class Paginator {
    pageIndex;
    pageLength;
    totalSize;
    constructor(pageIndex, pageLength, totalSize) {
        this.pageIndex = pageIndex;
        this.pageLength = pageLength;
        this.totalSize = totalSize;
    }
}

class RouterService {
    location;
    router;
    constructor(location, router) {
        this.location = location;
        this.router = router;
    }
    navigateBack(levelsToGoBack) {
        let urlSegments = this.location.path().split('/');
        urlSegments = urlSegments.slice(0, -levelsToGoBack);
        const newUrl = urlSegments.join('/');
        this.router.navigateByUrl(newUrl);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: RouterService, deps: [{ token: i1$3.Location }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: RouterService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: RouterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$3.Location }, { type: i2.Router }] });

class Search {
    filters;
    order;
    result;
    paginator;
    constructor(filters, order, result, paginator) {
        this.filters = filters;
        this.order = order;
        this.result = result;
        this.paginator = paginator;
    }
}

class Filter {
    condition;
    key;
    constructor(condition, key) {
        this.condition = condition;
        this.key = key;
    }
}
var FilterCondition;
(function (FilterCondition) {
    FilterCondition["Contains"] = "contains";
    FilterCondition["Between"] = "between";
    FilterCondition["EqualTo"] = "equal-to";
    FilterCondition["GreaterThan"] = "greater-than";
    FilterCondition["GreaterOrEqual"] = "greater-or-equal";
    FilterCondition["In"] = "in";
    FilterCondition["IsFalse"] = "is-false";
    FilterCondition["IsNull"] = "is-null";
    FilterCondition["IsNotNull"] = "is-not-null";
    FilterCondition["IsTrue"] = "is-true";
    FilterCondition["LessThan"] = "less-than";
    FilterCondition["LessOrEqual"] = "less-or-equal";
    FilterCondition["NotEqualTo"] = "not-equal-to";
    FilterCondition["NotIn"] = "not-in";
    FilterCondition["Or"] = "or";
    FilterCondition["StartsWith"] = "starts-with";
})(FilterCondition || (FilterCondition = {}));
class FilterOr extends Filter {
    values;
    constructor(condition, key, values) {
        super(condition, key);
        this.values = values;
    }
}
class FilterNoValue extends Filter {
    values;
    constructor(condition, key) {
        super(condition, key);
        this.values = undefined;
    }
}
class FilterNumberOneValue extends Filter {
    value;
    constructor(condition, key, value) {
        super(condition, key);
        this.value = value;
    }
}
class FilterNumberTwoValues extends Filter {
    firstValue;
    secondValue;
    constructor(condition, key, firstValue, secondValue) {
        super(condition, key);
        this.firstValue = firstValue;
        this.secondValue = secondValue;
    }
}
class FilterOneString extends Filter {
    value;
    constructor(condition, key, value) {
        super(condition, key);
        this.value = value;
    }
}
class FilterOneValue extends Filter {
    value;
    constructor(condition, key, value) {
        super(condition, key);
        this.value = value;
    }
}
class FilterMultiValues extends Filter {
    values;
    constructor(condition, key, values) {
        super(condition, key);
        this.values = values;
    }
}

class SearchService {
    filter(filters, data) {
        let filteredData = data;
        filters.forEach(filter => {
            switch (filter.condition) {
                case FilterCondition.Between:
                    filteredData = this.filterBetween(filter, filteredData);
                    break;
                case FilterCondition.Contains:
                    filteredData = this.filterContains(filter, filteredData);
                    break;
                case FilterCondition.Or:
                    filteredData = this.filterOr(filter, filteredData);
                    break;
                case FilterCondition.EqualTo:
                    filteredData = this.filterEqualTo(filter, filteredData);
                    break;
                case FilterCondition.IsFalse:
                    filteredData = this.filterIsFalse(filter, filteredData);
                    break;
                case FilterCondition.GreaterOrEqual:
                    filteredData = this.filterGreaterOrEqual(filter, filteredData);
                    break;
                case FilterCondition.GreaterThan:
                    filteredData = this.filterGreaterThan(filter, filteredData);
                    break;
                case FilterCondition.IsTrue:
                    filteredData = this.filterIsTrue(filter, filteredData);
                    break;
                case FilterCondition.LessOrEqual:
                    filteredData = this.filterLessOrEqual(filter, filteredData);
                    break;
                case FilterCondition.LessThan:
                    filteredData = this.filterLessThan(filter, filteredData);
                    break;
                case FilterCondition.NotEqualTo:
                    filteredData = this.filterNotEqualTo(filter, filteredData);
                    break;
                case FilterCondition.StartsWith:
                    filteredData = this.filterStartsWith(filter, filteredData);
                    break;
                default:
                    break;
            }
        });
        return filteredData;
    }
    getDaysInterval(days, mode) {
        let startDate = '', endDate = '';
        const today = new Date().toJSON().split('T')[0];
        if (mode === 'after') {
            startDate = days > 0 ? this.getTodayMinusXDays(days) : today;
            endDate = today;
        }
        else {
            startDate = this.getOldestDate();
            endDate = days > 0 ? this.getTodayMinusXDays(days) : today;
        }
        return {
            startDate: new Date(`${startDate} 00:00:00`).getTime(),
            endDate: new Date(`${endDate} 23:59:59`).getTime()
        };
    }
    paginate(items, page, size) {
        return items.slice((page - 1) * size, page * size);
    }
    toFilters(filters) {
        return filters.map(filter => this.toFilter(filter));
    }
    filterBetween(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value >= filter.firstValue && value <= filter.secondValue;
        });
    }
    filterContains(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value.toLowerCase().includes(filter.value.toLowerCase());
        });
    }
    filterOr(filter, data) {
        return data.filter(item => filter.values.some(subfilter => {
            switch (subfilter.condition) {
                case FilterCondition.Contains:
                    return this.filterContains(subfilter, [item]).length > 0;
                case FilterCondition.EqualTo:
                    return this.filterEqualTo(subfilter, [item]).length > 0;
                case FilterCondition.NotEqualTo:
                    return this.filterNotEqualTo(subfilter, [item]).length > 0;
                case FilterCondition.GreaterOrEqual:
                    return this.filterGreaterOrEqual(subfilter, [item]).length > 0;
                case FilterCondition.GreaterThan:
                    return this.filterGreaterThan(subfilter, [item]).length > 0;
                case FilterCondition.LessOrEqual:
                    return this.filterLessOrEqual(subfilter, [item]).length > 0;
                case FilterCondition.LessThan:
                    return this.filterLessThan(subfilter, [item]).length > 0;
                case FilterCondition.StartsWith:
                    return this.filterStartsWith(subfilter, [item]).length > 0;
                default:
                    return false;
            }
        }));
    }
    filterEqualTo(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            if (typeof value === 'string') {
                return value.toLowerCase() === filter.value.toLowerCase();
            }
            return value === filter.value;
        });
    }
    filterIsFalse(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return !value;
        });
    }
    filterGreaterOrEqual(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value >= filter.value;
        });
    }
    filterGreaterThan(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value > filter.value;
        });
    }
    filterIsTrue(filter, data) {
        return data.filter(row => row[filter.key]);
    }
    filterLessOrEqual(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value <= filter.value;
        });
    }
    filterLessThan(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value < filter.value;
        });
    }
    filterNotEqualTo(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            if (typeof value === 'string') {
                return value.toLowerCase() !== filter.value.toLowerCase();
            }
            return value !== filter.value;
        });
    }
    filterStartsWith(filter, data) {
        return data.filter(row => {
            const value = row[filter.key];
            return value.toLowerCase().startsWith(filter.value.toLowerCase());
        });
    }
    getOldestDate() {
        return new Date(new Date(1970, 0, 1).getTime()).toJSON().split('T')[0];
    }
    getTodayMinusXDays(days) {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toJSON().split('T')[0];
    }
    toFilter(filter) {
        switch (filter.type) {
            case SearchOptionType.BOOLEAN:
                return this.toFilterBoolean(filter);
            case SearchOptionType.DATE_RANGE:
                return this.toFilterDateRange(filter);
            case SearchOptionType.NUMBER:
                return this.toFilterNumber(filter);
            case SearchOptionType.SELECT:
                return this.toFilterSelect(filter);
            case SearchOptionType.TEXT:
            case SearchOptionType.TEXT_EXACTLY:
            case SearchOptionType.TEXT_FULL:
                return this.toFilterText(filter);
            default:
                return this.toFilterText(filter);
        }
    }
    toFilterBoolean(filter) {
        const { key } = filter, condition = this.toFilterCondition(filter.mode, filter.value);
        switch (condition) {
            case FilterCondition.IsTrue:
            case FilterCondition.IsFalse:
                return new FilterNoValue(condition, key);
            default:
                return new Filter(condition, key);
        }
    }
    toFilterCondition(mode, value) {
        switch (mode) {
            case SearchMode.contains:
                return FilterCondition.Contains;
            case SearchMode.boolean:
                return value === 'true' ? FilterCondition.IsTrue : FilterCondition.IsFalse;
            case SearchMode.exactly:
                return FilterCondition.EqualTo;
            case SearchMode.is:
                return FilterCondition.EqualTo;
            case SearchMode.isNot:
            case SearchMode.notExactly:
                return FilterCondition.NotEqualTo;
            case SearchMode.from:
                return FilterCondition.Between;
            case SearchMode.greaterOrEqual:
                return FilterCondition.GreaterOrEqual;
            case SearchMode.greaterThan:
                return FilterCondition.GreaterThan;
            case SearchMode.lessOrEqual:
                return FilterCondition.LessOrEqual;
            case SearchMode.lessThan:
                return FilterCondition.LessThan;
            case SearchMode.startsWith:
                return FilterCondition.StartsWith;
            default:
                return FilterCondition.Contains;
        }
    }
    toFilterDateRange(filter) {
        const { key } = filter, condition = this.toFilterCondition(filter.mode, filter.value);
        const { from, to } = JSON.parse(filter.value);
        if (condition === FilterCondition.Between) {
            return new FilterNumberTwoValues(condition, key, from, to);
        }
        return new Filter(condition, key);
    }
    toFilterNumber(filter) {
        const { key, value } = filter, condition = this.toFilterCondition(filter.mode, filter.value);
        switch (condition) {
            case FilterCondition.GreaterOrEqual:
            case FilterCondition.GreaterThan:
            case FilterCondition.LessOrEqual:
            case FilterCondition.LessThan:
                return new FilterNumberOneValue(condition, key, Number(value));
            case FilterCondition.EqualTo:
            case FilterCondition.NotEqualTo:
                return new FilterOneValue(condition, key, value);
            default:
                return new Filter(condition, key);
        }
    }
    toFilterSelect(filter) {
        const { key, value } = filter, condition = this.toFilterCondition(filter.mode, filter.value);
        switch (condition) {
            case FilterCondition.EqualTo:
            case FilterCondition.NotEqualTo:
                return new FilterOneValue(condition, key, value);
            default:
                return new Filter(condition, key);
        }
    }
    toFilterText(filter) {
        const { key, value } = filter, condition = this.toFilterCondition(filter.mode, filter.value);
        switch (condition) {
            case FilterCondition.Contains:
            case FilterCondition.StartsWith:
                return new FilterOneString(condition, key, value);
            case FilterCondition.EqualTo:
            case FilterCondition.NotEqualTo:
                return new FilterOneValue(condition, key, value);
            default:
                return new Filter(condition, key);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SearchService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SearchService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: SearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class TableConfig {
    columns;
    name;
    constructor({ columns, name }) {
        this.columns = columns;
        this.name = name;
    }
}
class ColumnConfig {
    currentWidth;
    maxWidth;
    minWidth;
    name;
    visible;
    constructor({ currentWidth, maxWidth, minWidth, name, visible }) {
        this.currentWidth = currentWidth;
        this.maxWidth = maxWidth;
        this.minWidth = minWidth;
        this.name = name;
        this.visible = visible;
    }
}

class TableService {
    environment;
    cookieName;
    constructor(environment) {
        this.environment = environment;
        this.cookieName = `${this.environment.cookieName}-local-config`;
    }
    getColumns(tableName) {
        const tableConfig = this.getTableConfig(tableName), columns = tableConfig?.columns || [];
        return columns;
    }
    updateColumn(tableName, column) {
        const tableConfig = this.getTableConfig(tableName), columnIndex = tableConfig.columns.findIndex(c => c.name === column.name);
        if (columnIndex !== -1) {
            tableConfig.columns.splice(columnIndex, 1, column);
            tableConfig.columns = this.calculateWidths(tableConfig.columns);
            this.updateTableConfig(tableConfig);
        }
    }
    updateColumns(tableName, columns) {
        const tableConfig = this.getTableConfig(tableName);
        tableConfig.columns = this.calculateWidths(columns);
        this.updateTableConfig(tableConfig);
    }
    calculateWidths(columns) {
        const visibleColumns = columns.filter(column => column.visible);
        const totalCurrentWidth = visibleColumns.reduce((sum, column) => sum + column.currentWidth, 0);
        const remainingWidth = 100 - totalCurrentWidth;
        if (remainingWidth > 0) {
            this.distributeExtraWidth(visibleColumns, remainingWidth);
        }
        else if (remainingWidth < 0) {
            this.reduceExcessWidth(visibleColumns, Math.abs(remainingWidth));
        }
        return columns;
    }
    distributeExtraWidth(visibleColumns, extraWidth) {
        let remainingWidth = extraWidth;
        const columnsNotAtMax = visibleColumns.filter(column => column.currentWidth < column.maxWidth);
        let extraWidthPerColumn = remainingWidth / (columnsNotAtMax.length > 0 ? columnsNotAtMax.length : visibleColumns.length);
        // eslint-disable-next-line no-loops/no-loops
        for (const column of columnsNotAtMax) {
            if (remainingWidth <= 0) {
                break;
            }
            const newWidth = column.currentWidth + extraWidthPerColumn;
            if (newWidth <= column.maxWidth) {
                column.currentWidth = newWidth;
                const actualWidthAdded = Math.min(extraWidthPerColumn, remainingWidth);
                remainingWidth -= actualWidthAdded;
            }
        }
        if (remainingWidth > 0) {
            extraWidthPerColumn = remainingWidth / visibleColumns.length;
            // eslint-disable-next-line no-loops/no-loops
            for (const column of visibleColumns) {
                if (remainingWidth <= 0) {
                    break;
                }
                const actualWidthAdded = Math.min(extraWidthPerColumn, remainingWidth);
                column.currentWidth += actualWidthAdded;
                remainingWidth -= actualWidthAdded;
            }
        }
    }
    getTableConfig(tableName) {
        const localConfig = window.localStorage.getItem(this.cookieName);
        let tableConfig;
        if (localConfig) {
            const decodedConfig = JSON.parse(atob(localConfig));
            tableConfig = decodedConfig.tables?.find(table => table.name === tableName);
        }
        if (tableConfig) {
            return tableConfig;
        }
        return new TableConfig({ columns: [], name: tableName });
    }
    reduceExcessWidth(visibleColumns, excessWidth) {
        let missingWidth = excessWidth;
        const columnsAboveMax = visibleColumns.filter(column => column.currentWidth > column.maxWidth);
        let extraWidthPerColumn = missingWidth / (columnsAboveMax.length > 0 ? columnsAboveMax.length : visibleColumns.length);
        // eslint-disable-next-line no-loops/no-loops
        for (const column of columnsAboveMax) {
            if (missingWidth <= 0) {
                break;
            }
            const newWidth = column.currentWidth - extraWidthPerColumn;
            if (newWidth >= column.maxWidth) {
                column.currentWidth = newWidth;
                const actualWidthSubtracted = Math.min(extraWidthPerColumn, missingWidth);
                missingWidth -= actualWidthSubtracted;
            }
        }
        if (missingWidth > 0) {
            extraWidthPerColumn = missingWidth / visibleColumns.length;
            // eslint-disable-next-line no-loops/no-loops
            for (const column of visibleColumns) {
                if (missingWidth <= 0) {
                    break;
                }
                const actualWidthAdded = Math.min(extraWidthPerColumn, missingWidth);
                column.currentWidth -= actualWidthAdded;
                missingWidth -= actualWidthAdded;
            }
        }
    }
    setConfigCookie(config) {
        window.localStorage.setItem(this.cookieName, btoa(JSON.stringify(config)));
    }
    updateTableConfig(tableConfig) {
        const localConfig = window.localStorage.getItem(this.cookieName);
        if (localConfig) {
            const decodedConfig = JSON.parse(atob(localConfig)), tableIndex = decodedConfig.tables?.findIndex(table => table.name === tableConfig.name);
            if (tableIndex !== undefined && tableIndex !== -1) {
                decodedConfig.tables?.splice(tableIndex, 1, tableConfig);
            }
            else {
                if (decodedConfig.tables === undefined) {
                    decodedConfig.tables = [];
                }
                decodedConfig.tables?.push(tableConfig);
            }
            this.setConfigCookie(decodedConfig);
        }
        else {
            const newConfig = new LocalConfig({ tables: [tableConfig] });
            this.setConfigCookie(newConfig);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: TableService, deps: [{ token: 'environment' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: TableService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.3", ngImport: i0, type: TableService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: HttpEnvironment, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }] });

class File {
    name;
    content;
    contentType;
    constructor(name, content, contentType) {
        this.name = name;
        this.content = content;
        this.contentType = contentType;
    }
}
var FileFormat;
(function (FileFormat) {
    FileFormat["Csv"] = "csv";
    FileFormat["Pdf"] = "pdf";
})(FileFormat || (FileFormat = {}));

class Order {
    field;
    orderType;
    constructor(field, orderType) {
        this.field = field;
        this.orderType = orderType;
    }
}
var OrderType;
(function (OrderType) {
    OrderType["Asc"] = "asc";
    OrderType["Desc"] = "desc";
})(OrderType || (OrderType = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { CardInfoFiltersConfig, CardsInfoFiltersConfig, CardsInfoFiltersService, ColumnConfig, Constants, Credentials, CredentialsCookiesService, CustomHttpOptions, CustomHttpResponse, File, FileFormat, Filter, FilterCondition, FilterNumberOneValue, FilterNumberTwoValues, FilterOneValue, FilterOr, HttpEnvironment, HttpService, LincesCommonModule, LoginService, Order, OrderType, PageWithTabsComponent, Paginator, Provider, ProviderTitle, RouterService, Search, SearchService, SessionCookie, SessionCookieService, TableConfig, TableService, User, UserRole, UtilService };
